import React, { useMemo } from 'react';
import { CalendarCheck } from '@phosphor-icons/react';

interface AttendanceRow {
    employeeName: string;
    eventName: string;
    attendanceStatus: string;
}

interface TeamEventAttendanceWidgetProps {
    data?: AttendanceRow[] | null;
    loading?: boolean;
    error?: string;
}

const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'attended') return { label: 'Attended', className: 'bg-emerald-100 text-emerald-700' };
    if (s === 'upcoming' || s === 'registered') return { label: 'Upcoming', className: 'bg-blue-100 text-blue-700' };
    if (s === 'no_show' || s === 'no-show' || s === 'absent') return { label: 'No-Show', className: 'bg-red-100 text-red-700' };
    return { label: status || 'Unknown', className: 'bg-gray-100 text-gray-600' };
};

export const TeamEventAttendanceWidget: React.FC<TeamEventAttendanceWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    const stats = useMemo(() => {
        let attended = 0, upcoming = 0, noShow = 0;
        rows.forEach(r => {
            const s = (r.attendanceStatus || '').toLowerCase();
            if (s === 'attended') attended++;
            else if (s === 'upcoming' || s === 'registered') upcoming++;
            else if (s === 'no_show' || s === 'no-show' || s === 'absent') noShow++;
        });
        return { attended, upcoming, noShow };
    }, [rows]);

    return (
        <div className="flex flex-col h-full">
<div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <CalendarCheck className="w-8 h-8" />
                        <span className="text-sm">No event data</span>
                    </div>
                ) : (
                    <>
                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="text-center p-2 bg-emerald-50 rounded-lg">
                                <div className="text-lg font-bold text-emerald-600">{stats.attended}</div>
                                <div className="text-[10px] text-gray-400">Attended</div>
                            </div>
                            <div className="text-center p-2 bg-blue-50 rounded-lg">
                                <div className="text-lg font-bold text-blue-600">{stats.upcoming}</div>
                                <div className="text-[10px] text-gray-400">Upcoming</div>
                            </div>
                            <div className="text-center p-2 bg-red-50 rounded-lg">
                                <div className="text-lg font-bold text-red-600">{stats.noShow}</div>
                                <div className="text-[10px] text-gray-400">No-Show</div>
                            </div>
                        </div>

                        {/* Attendance Table */}
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] text-gray-400 uppercase tracking-wider">
                                    <th className="pb-2 font-medium">Person</th>
                                    <th className="pb-2 font-medium">Event</th>
                                    <th className="pb-2 font-medium text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => {
                                    const badge = getStatusBadge(row.attendanceStatus);
                                    return (
                                        <tr key={idx}>
                                            <td className="py-1.5 pr-2 text-gray-800 truncate max-w-[100px]">{row.employeeName}</td>
                                            <td className="py-1.5 pr-2 text-gray-600 truncate max-w-[120px]">{row.eventName}</td>
                                            <td className="py-1.5 text-right">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.className}`}>
                                                    {badge.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
};
